import { getAuthToken } from "./authToken";

const PROTECTED_PATH_PREFIXES = ["/files/", "/static/", "/temp_files/"];

function getApiOrigin(): string | null {
  const base = process.env.REACT_APP_BASE_URL;
  if (!base) return null;

  try {
    return new URL(base).origin;
  } catch {
    return null;
  }
}

function normalizePathname(pathname: string): string {
  return pathname.replace(/^\/api(?=\/)/, "");
}

/**
 * Local dev only: fetch protected files via CRA proxy (same origin) to avoid CORS.
 */
function resolveFetchUrl(url: string): string {
  if (process.env.NODE_ENV !== "development" || !isProtectedApiFileUrl(url)) {
    return url;
  }

  try {
    const resolved = new URL(url, process.env.REACT_APP_BASE_URL);
    return `${resolved.pathname}${resolved.search}`;
  } catch {
    return url;
  }
}

export function isProtectedApiFileUrl(url: string): boolean {
  if (!url || url.startsWith("blob:") || url.startsWith("data:")) {
    return false;
  }

  const apiOrigin = getApiOrigin();
  if (!apiOrigin) return false;

  try {
    const resolved = new URL(url, process.env.REACT_APP_BASE_URL);
    if (resolved.origin !== apiOrigin) return false;

    const pathname = normalizePathname(resolved.pathname);
    return PROTECTED_PATH_PREFIXES.some((prefix) =>
      pathname.startsWith(prefix),
    );
  } catch {
    return false;
  }
}

export async function fetchAuthenticatedFile(
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  const token = getAuthToken();
  const headers = new Headers(init.headers);

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return fetch(resolveFetchUrl(url), {
    ...init,
    headers,
    credentials: "include",
    cache: process.env.NODE_ENV === "development" ? "no-store" : "default",
  });
}

export async function fetchAuthenticatedFileBlob(
  url: string,
  options?: { timeoutMs?: number },
): Promise<Blob> {
  const timeoutMs = options?.timeoutMs ?? 45_000;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetchAuthenticatedFile(url, {
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }

    const blob = await response.blob();
    const contentType = response.headers.get("content-type") ?? blob.type;

    if (contentType.includes("text/html")) {
      throw new Error("Received HTML instead of a file (check dev proxy)");
    }

    return blob;
  } finally {
    window.clearTimeout(timer);
  }
}
